import React, { useState, useEffect, useCallback } from 'react';
import {
  MdSpeed,
  MdPerson,
  MdEdit,
  MdSave,
  MdCancel,
  MdRefresh,
  MdSearch,
  MdClose,
  MdLock,
  MdBarChart,
  MdArrowUpward,
  MdArrowDownward,
  MdSwapVert,
  MdCheck,
  MdWarning
} from 'react-icons/md';
import { canManageQuotas } from '../../utils/permissions';
import { useClass } from '../../utils/ClassContext';

const QuotaManagement = ({
  username,
  userRole,
  userName,
  apiBaseUrl,
  onNavigate
}) => {
  const { selectedClass } = useClass();
  const [quotaData, setQuotaData] = useState([]);
  const [editingRows, setEditingRows] = useState(new Set());
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(new Set());
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Environment selector
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvId, setSelectedEnvId] = useState(null); // null = class-level (legacy)
  const [envDefaultQuota, setEnvDefaultQuota] = useState(10);
  const [savingEnvDefault, setSavingEnvDefault] = useState(false);
  const [editingEnvDefault, setEditingEnvDefault] = useState(false);
  const [envDefaultEdit, setEnvDefaultEdit] = useState(10);

  // Check if user has permission to manage quotas
  const hasPermission = canManageQuotas(userRole);

  const fetchQuotaData = useCallback(async () => {
    if (!selectedClass) {
      setQuotaData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let url = `${apiBaseUrl}/quota-data?class_id=${selectedClass.class_id}`;
      if (selectedEnvId) url += `&environment_id=${encodeURIComponent(selectedEnvId)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setQuotaData(data.quotas || []);
          // Always update environments list from server
          if (data.environments) {
            setEnvironments(data.environments);
          }
          // Update env default quota display
          if (selectedEnvId && data.environments) {
            const env = data.environments.find(e => e.environment_id === selectedEnvId);
            if (env) {
              setEnvDefaultQuota(env.default_quota ?? 10);
              setEnvDefaultEdit(env.default_quota ?? 10);
            }
          }
        } else {
          setError(data.error || 'Failed to fetch quota data');
        }
      } else {
        setError('Failed to fetch quota data');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, userRole, username, selectedClass, selectedEnvId]); // eslint-disable-line

  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    // Only fetch if we have valid userRole and username
    if (userRole && username) {
      fetchQuotaData();
    } else {
      setLoading(false);
    }
  }, [hasPermission, apiBaseUrl, fetchQuotaData, userRole, username]);

  // Handle editing state
  const startEditing = (username) => {
    const newEditingRows = new Set(editingRows);
    newEditingRows.add(username);
    setEditingRows(newEditingRows);

    // Initialize edit value with current quota
    const currentQuota = quotaData.find(item => item.username === username)?.quota_value || 0;
    setEditValues(prev => ({
      ...prev,
      [username]: currentQuota
    }));
  };

  const cancelEditing = (username) => {
    const newEditingRows = new Set(editingRows);
    newEditingRows.delete(username);
    setEditingRows(newEditingRows);

    // Remove edit value
    const newEditValues = { ...editValues };
    delete newEditValues[username];
    setEditValues(newEditValues);
  };

  const updateEditValue = (username, value) => {
    const parsedValue = parseInt(value);
    setEditValues(prev => ({
      ...prev,
      [username]: isNaN(parsedValue) ? 0 : parsedValue
    }));
  };

  const saveQuota = async (username) => {
    const newValue = editValues[username];
    if (newValue === undefined) return;

    try {
      const newSaving = new Set(saving);
      newSaving.add(username);
      setSaving(newSaving);

      const body = {
        username: username,
        quota_value: newValue,
        class_id: selectedClass.class_id,
      };
      if (selectedEnvId) body.environment_id = selectedEnvId;

      const response = await fetch(`${apiBaseUrl}/quota`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        // Update local data
        setQuotaData(prev =>
          prev.map(item =>
            item.username === username
              ? { ...item, quota_value: newValue }
              : item
          )
        );

        // Exit editing mode
        cancelEditing(username);

        // Show success message
        setSuccessMessage(`Quota updated for ${username}`);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update quota');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      const newSaving = new Set(saving);
      newSaving.delete(username);
      setSaving(newSaving);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort quota data based on current sort config
  const sortedQuotaData = React.useMemo(() => {
    if (!sortConfig.key) return quotaData;

    return [...quotaData].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (sortConfig.direction === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  }, [quotaData, sortConfig]);

  // Filter quota data based on search term
  const filteredQuotaData = React.useMemo(() => {
    if (!searchTerm) return sortedQuotaData;

    return sortedQuotaData.filter(item =>
      Object.values(item).some(value =>
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [sortedQuotaData, searchTerm]);

  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <MdSwapVert className="text-gray-400 text-lg" />;
    }
    return sortConfig.direction === 'asc' ?
      <MdArrowUpward className="text-blue-600 text-lg" /> :
      <MdArrowDownward className="text-blue-600 text-lg" />;
  };

  // Render access denied message
  if (!hasPermission) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center py-8">
            <MdLock className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You need instructor privileges to manage user quotas.
            </p>
            <button
              onClick={() => onNavigate('home')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition-colors duration-200"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render no class selected message
  if (!selectedClass) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center py-8">
            <MdSpeed className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Class Selected</h2>
            <p className="text-gray-600 mb-4">
              Please select a class to manage quotas.
            </p>
            <button
              onClick={() => onNavigate('home')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition-colors duration-200"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Quota Management</h1>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading quota data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Quota Management</h1>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <MdWarning className="text-2xl text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
            <button
              onClick={fetchQuotaData}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Quota Management</h1>
            <p className="text-gray-600">
              {selectedClass ? 
                `Manage feedback quotas for users in ${selectedClass.class_name} (${selectedClass.class_code})` : 
                'No class selected'
              }
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
              <MdSpeed className="text-white text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <MdCheck className="text-green-600 text-xl mr-3" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* ERD Environments — listed as selectable tabs */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-800">ERD Assignment (Environment)</h2>
            <p className="text-xs text-gray-500 mt-0.5">Select an environment to view and edit its quotas</p>
          </div>
          {environments.length === 0 && !loading && (
            <span className="text-xs text-gray-400 italic">No environments found for this class</span>
          )}
        </div>

        {/* Environment list */}
        <div className="flex flex-wrap gap-2">
          {/* "All / class-level" pill */}
          <button
            onClick={() => { setSelectedEnvId(null); setEditingEnvDefault(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              selectedEnvId === null
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500 hover:bg-gray-50'
            }`}
          >
            Class-level (all envs)
          </button>

          {environments.map(env => (
            <button
              key={env.environment_id}
              onClick={() => {
                setSelectedEnvId(env.environment_id);
                setEnvDefaultQuota(env.default_quota ?? 10);
                setEnvDefaultEdit(env.default_quota ?? 10);
                setEditingEnvDefault(false);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-2 ${
                selectedEnvId === env.environment_id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <span>{env.name || env.environment_id}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded font-normal ${
                selectedEnvId === env.environment_id
                  ? 'bg-blue-500 text-blue-100'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {env.default_quota === -1 ? '∞' : env.default_quota} default
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded font-normal ${
                selectedEnvId === env.environment_id
                  ? 'bg-blue-700 text-blue-100'
                  : 'bg-orange-50 text-orange-600 border border-orange-200'
              }`}>
                {env.used ?? 0} used
              </span>
            </button>
          ))}
        </div>

        {/* Default quota editor — shown when an env is selected */}
        {selectedEnvId && (
          <div className="mt-4 flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">
                Default quota for <strong>{environments.find(e => e.environment_id === selectedEnvId)?.name || selectedEnvId}</strong>
              </label>
              {editingEnvDefault ? (
                <input
                  type="number"
                  min="-1"
                  value={envDefaultEdit}
                  onChange={e => setEnvDefaultEdit(parseInt(e.target.value) || 0)}
                  className="w-24 px-2 py-1 border border-blue-300 rounded text-sm"
                />
              ) : (
                <span className="text-sm font-semibold text-blue-900">
                  {envDefaultQuota === -1 ? '∞ unlimited' : `${envDefaultQuota} requests`}
                </span>
              )}
              {(() => {
                const selEnv = environments.find(e => e.environment_id === selectedEnvId);
                const used = selEnv?.used ?? 0;
                return used > 0 ? (
                  <span className="ml-3 text-xs text-orange-700 font-medium">{used} total used by all students</span>
                ) : null;
              })()}
            </div>
            {editingEnvDefault ? (
              <div className="flex gap-2">
                <button
                  disabled={savingEnvDefault}
                  onClick={async () => {
                    setSavingEnvDefault(true);
                    try {
                      const res = await fetch(
                        `${apiBaseUrl}/environments/${encodeURIComponent(selectedEnvId)}/quota`,
                        {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', 'User-Role': userRole, 'Requesting-User': username },
                          credentials: 'include',
                          body: JSON.stringify({ class_code: selectedClass.class_code, default_quota: envDefaultEdit }),
                        }
                      );
                      const d = await res.json();
                      if (d.success) {
                        setEnvDefaultQuota(envDefaultEdit);
                        setEnvironments(prev => prev.map(e =>
                          e.environment_id === selectedEnvId ? { ...e, default_quota: envDefaultEdit } : e
                        ));
                        setSuccessMessage('Default quota updated');
                        setTimeout(() => setSuccessMessage(''), 3000);
                        setEditingEnvDefault(false);
                      } else setError(d.error || 'Failed to update default quota');
                    } catch { setError('Network error'); }
                    finally { setSavingEnvDefault(false); }
                  }}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingEnvDefault ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => { setEditingEnvDefault(false); setEnvDefaultEdit(envDefaultQuota); }}
                  className="px-3 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            ) : (
              <button onClick={() => setEditingEnvDefault(true)}
                className="px-3 py-1 text-xs bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-100">
                Edit default
              </button>
            )}
          </div>
        )}
      </div>

      {/* Stats and Controls Card */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdBarChart className="text-blue-600 text-2xl" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">
                Total Users: {quotaData.length}
              </h3>
              <p className="text-sm text-gray-600">
                {filteredQuotaData.length} shown after filtering
                {selectedEnvId && <span className="ml-1 text-blue-600">· {selectedEnvId}</span>}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full sm:w-auto">
            <div className="flex-1 sm:flex-none sm:w-80">
              <label htmlFor="search" className="sr-only">Search users</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdSearch className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-400"
                />
                {searchTerm && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      title="Clear search"
                    >
                      <MdClose className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchQuotaData}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                title="Refresh quota data"
              >
                <MdRefresh className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quota Data Table */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">User Quotas</h2>
          <p className="text-sm text-gray-600 mt-1">
            Click the edit button to modify a user's feedback quota. Use -1 for unlimited quotas.
          </p>
        </div>

        {filteredQuotaData.length === 0 ? (
          <div className="text-center py-12">
            <MdSpeed className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              {quotaData.length === 0 ? 'No Quota Data Found' : 'No Results Found'}
            </h3>
            <p className="text-gray-600">
              {quotaData.length === 0
                ? 'No user quota data is available.'
                : 'Try adjusting your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('username')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Username</span>
                      {getSortIndicator('username')}
                    </div>
                  </th>
                  {selectedEnvId && (
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => handleSort('quota_used')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Used</span>
                        {getSortIndicator('quota_used')}
                      </div>
                    </th>
                  )}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('quota_value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{selectedEnvId ? 'Limit' : 'Quota Value'}</span>
                      {getSortIndicator('quota_value')}
                    </div>
                  </th>
                  {selectedEnvId && (
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => handleSort('quota_remaining')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Remaining</span>
                        {getSortIndicator('quota_remaining')}
                      </div>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuotaData.map((item, index) => {
                  const used      = item.quota_used ?? 0;
                  const limit     = item.quota_value;
                  const remaining = item.quota_remaining ?? (limit === -1 ? -1 : Math.max(0, limit - used));
                  const exhausted = limit !== -1 && remaining <= 0;
                  return (
                  <tr
                    key={item.username || index}
                    className={`transition-colors duration-200 ${exhausted ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <MdPerson className="text-blue-600 text-sm" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {item.username || 'N/A'}
                        </div>
                      </div>
                    </td>
                    {selectedEnvId && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          exhausted
                            ? 'bg-red-100 text-red-800'
                            : used > 0
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {used}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingRows.has(item.username) ? (
                        <input
                          type="number"
                          min="-1"
                          value={editValues[item.username] || 0}
                          onChange={(e) => updateEditValue(item.username, e.target.value)}
                          className="w-20 px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          title="Enter -1 for unlimited quota"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">
                          {limit === -1 ? '∞' : (limit || 0)}
                        </div>
                      )}
                    </td>
                    {selectedEnvId && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${
                          exhausted ? 'text-red-700' : remaining <= 2 ? 'text-amber-700' : 'text-green-700'
                        }`}>
                          {remaining === -1 ? '∞' : remaining}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {editingRows.has(item.username) ? (
                          <>
                            <button
                              onClick={() => saveQuota(item.username)}
                              disabled={saving.has(item.username)}
                              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors duration-200 disabled:opacity-50"
                              title="Save changes"
                            >
                              {saving.has(item.username) ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                              ) : (
                                <MdSave className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => cancelEditing(item.username)}
                              disabled={saving.has(item.username)}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors duration-200 disabled:opacity-50"
                              title="Cancel editing"
                            >
                              <MdCancel className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => startEditing(item.username)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors duration-200"
                            title="Edit quota"
                          >
                            <MdEdit className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {filteredQuotaData.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center text-sm text-gray-600">
            Showing {filteredQuotaData.length} of {quotaData.length} user quotas
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotaManagement;